import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Boxes, Calculator, ImageOff, Loader2, Undo2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Field, Section, Toggle } from '@/components/ui/editor-fields';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { formatDateTime, formatNumber, initials } from '@/lib/format';
import { BreakdownDialog } from './breakdown-dialog';
import { fromInputValue } from '@/lib/store-time';
import { cn } from '@/lib/utils';
import {
  PAGE_SIZE,
  useInventoryHistory,
  useInventoryItem,
  useRecalculateQoh,
  useRegisterMovement,
  useRevertMovement,
  useUpdateFlags,
} from './inventory.api';
import {
  MOVEMENT_REASON,
  MOVEMENT_TYPE,
  isIncrease,
  itemLabel,
  packBreakdown,
  type InventoryMovement,
} from './types';

/**
 * One product's stock in one store.
 *
 * Three jobs that the original ran together down a single 1,000-line modal:
 * moving stock, changing the item's settings, and reading what has happened to
 * it. They are tabs, because they are answers to different questions and only
 * one of them is destructive.
 */

/** The three a person performs; `Sale` is the till's and never offered here. */
const MANUAL_TYPES = [
  MOVEMENT_TYPE.receiving,
  MOVEMENT_TYPE.adjustmentUp,
  MOVEMENT_TYPE.adjustmentDown,
] as const;

export function ItemDrawer({
  businessItemId,
  open,
  onOpenChange,
}: {
  businessItemId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();

  const [historyPage, setHistoryPage] = useState(0);
  const [reverting, setReverting] = useState<InventoryMovement | null>(null);

  // Movement form.
  const [type, setType] = useState<number>(MOVEMENT_TYPE.receiving);
  const [reason, setReason] = useState<number>(MOVEMENT_REASON.none);
  const [amount, setAmount] = useState('');
  const [comments, setComments] = useState('');
  const [changedAt, setChangedAt] = useState('');

  /*
   * The two entry helpers the original had beside the amount box.
   *
   * `boxes × units` is for receiving a delivery counted in cases; `countedTo`
   * is for a stocktake — you type what you counted and it works out both the
   * direction and the difference, which is the arithmetic people get wrong.
   */
  const [boxes, setBoxes] = useState('');
  const [boxUnits, setBoxUnits] = useState('');
  const [countedTo, setCountedTo] = useState('');
  const [breakingDown, setBreakingDown] = useState(false);

  // Settings form.
  const [cost, setCost] = useState('');
  const [costOfBox, setCostOfBox] = useState('');
  const [pack, setPack] = useState('');

  const item = useInventoryItem(open ? businessItemId : null);
  const history = useInventoryHistory(open ? businessItemId : null, historyPage);
  const register = useRegisterMovement();
  const recalculate = useRecalculateQoh();
  const revert = useRevertMovement();
  const flags = useUpdateFlags();

  const data = item.data;

  useEffect(() => {
    setHistoryPage(0);
    setType(MOVEMENT_TYPE.receiving);
    setReason(MOVEMENT_REASON.none);
    setAmount('');
    setComments('');
    setChangedAt('');
    setBoxes('');
    setBoxUnits('');
    setCountedTo('');
  }, [businessItemId]);

  useEffect(() => {
    setCost(data ? String(data.cost) : '');
    setCostOfBox(data ? String(data.costOfBox) : '');
    setPack(data ? String(data.pack) : '');
  }, [data]);

  const busy =
    register.isPending ||
    revert.isPending ||
    flags.isPending ||
    recalculate.isPending;
  const parsedAmount = Number(amount);
  const canRegister =
    amount.trim() !== '' && Number.isFinite(parsedAmount) && parsedAmount > 0;

  const failed = (error: unknown) =>
    toast.error(
      error instanceof Error ? error.message : t('inventory.actionFailed')
    );

  function submitMovement() {
    if (!businessItemId) return;
    register.mutate(
      {
        businessItemId,
        amount: parsedAmount,
        comments,
        inventoryChangeDate: changedAt ? fromInputValue(changedAt) : null,
        type,
        reason,
      },
      {
        onSuccess: () => {
          toast.success(t('inventory.movementRecorded'));
          setAmount('');
          setComments('');
          setChangedAt('');
          setHistoryPage(0);
        },
        onError: failed,
      }
    );
  }

  const rows = history.data?.result ?? [];
  const packs = data ? packBreakdown(data.quantityOnHand, data.unitsPerPackage) : null;
  const boxTotal = (Number(boxes) || 0) * (Number(boxUnits) || 0);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-xl"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle className="text-base">
            {data ? itemLabel(data) : t('inventory.item')}
          </SheetTitle>
          {item.isPending ? (
            <Skeleton className="h-7 w-32" />
          ) : (
            data && (
              <div className="flex items-center gap-3">
                {data.itemImage ? (
                  <img
                    src={data.itemImage}
                    alt=""
                    className="bg-muted size-12 shrink-0 rounded-lg object-contain"
                  />
                ) : (
                  <div className="bg-muted text-muted-foreground flex size-12 shrink-0 items-center justify-center rounded-lg">
                    <ImageOff className="size-4" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-2xl font-semibold tabular-nums">
                    {formatNumber(data.quantityOnHand)}
                    <span className="text-muted-foreground ml-1.5 text-sm font-normal">
                      {t('inventory.onHand')}
                    </span>
                  </p>
                  {packs && (
                    <p className="text-muted-foreground text-xs">
                      {t('inventory.packsAndUnits', {
                        packs: formatNumber(packs.packs),
                        units: formatNumber(packs.units),
                      })}
                    </p>
                  )}
                  <p className="text-muted-foreground truncate text-xs">
                    {data.upc}
                    {data.sectionName && ` · ${data.sectionName}`}
                  </p>
                </div>
                <div className="ml-auto flex shrink-0 flex-col gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy || !data.affectsInventory}
                    title={
                      data.affectsInventory
                        ? undefined
                        : t('inventory.recalculateUntracked')
                    }
                    onClick={() =>
                      recalculate.mutate(data.id, {
                        onSuccess: () =>
                          toast.success(t('inventory.recalculated')),
                        onError: failed,
                      })
                    }
                  >
                    {recalculate.isPending ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <Calculator className="size-3.5" />
                    )}
                    {t('inventory.recalculate')}
                  </Button>
                  {data.hasRelatedItems && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() => setBreakingDown(true)}
                    >
                      <Boxes className="size-3.5" />
                      {t('inventory.breakdown')}
                    </Button>
                  )}
                </div>
              </div>
            )
          )}
        </SheetHeader>

        {/* The tabs arrive with the item; without this the body stays blank
            under the header skeleton. */}
        {item.isLoading && (
          <div className="space-y-4 p-4">
            <Skeleton className="h-9 w-64" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-9 w-full" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-9 w-full" />
            </div>
            <Skeleton className="h-20 w-full" />
          </div>
        )}

        {item.isError && (
          <p className="text-destructive p-4 text-sm">
            {t('inventory.itemFailed')}
          </p>
        )}

        {data && (
          <div className="p-4">
            <Tabs defaultValue="move">
              <TabsList>
                <TabsTrigger value="move">{t('inventory.tabMove')}</TabsTrigger>
                <TabsTrigger value="settings">
                  {t('inventory.tabSettings')}
                </TabsTrigger>
                <TabsTrigger value="history">
                  {t('inventory.tabHistory')}
                </TabsTrigger>
              </TabsList>

              {/* ------------------------------------------------- move */}
              <TabsContent value="move" className="space-y-4 pt-4">
                <Field label={t('inventory.movementType')}>
                  <Select
                    value={String(type)}
                    disabled={busy}
                    onValueChange={(value) => setType(Number(value))}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MANUAL_TYPES.map((value) => (
                        <SelectItem key={value} value={String(value)}>
                          {t(`inventory.type${value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <div className="bg-muted/50 space-y-3 rounded-xl p-3">
                  <p className="text-xs font-semibold">
                    {t('inventory.helpers')}
                  </p>

                  <div className="flex flex-wrap items-end gap-2">
                    <label className="text-muted-foreground space-y-1 text-xs">
                      {t('inventory.boxes')}
                      <Input
                        type="number"
                        min={0}
                        step="1"
                        className="h-9 w-20"
                        value={boxes}
                        disabled={busy}
                        onChange={(event) => setBoxes(event.target.value)}
                      />
                    </label>
                    <span className="pb-2 text-sm">×</span>
                    <label className="text-muted-foreground space-y-1 text-xs">
                      {t('inventory.unitsPerBox')}
                      <Input
                        type="number"
                        min={0}
                        step="1"
                        className="h-9 w-20"
                        value={boxUnits}
                        disabled={busy}
                        onChange={(event) => setBoxUnits(event.target.value)}
                      />
                    </label>
                    <span className="pb-2 text-sm tabular-nums">
                      = {formatNumber(boxTotal)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mb-0.5"
                      disabled={busy || boxTotal <= 0}
                      onClick={() => setAmount(String(boxTotal))}
                    >
                      {t('inventory.useIt')}
                    </Button>
                  </div>

                  <div className="flex flex-wrap items-end gap-2">
                    <label className="text-muted-foreground space-y-1 text-xs">
                      {t('inventory.countedTo')}
                      <Input
                        type="number"
                        min={0}
                        step="any"
                        className="h-9 w-24"
                        value={countedTo}
                        disabled={busy}
                        onChange={(event) => setCountedTo(event.target.value)}
                      />
                    </label>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mb-0.5"
                      disabled={busy || countedTo.trim() === ''}
                      onClick={() => {
                        const target = Number(countedTo);
                        if (!Number.isFinite(target)) return;
                        const difference = target - data.quantityOnHand;
                        // The direction is part of the answer, not a second
                        // question — getting it wrong is how a stocktake
                        // doubles a discrepancy instead of closing it.
                        setType(
                          difference >= 0
                            ? MOVEMENT_TYPE.adjustmentUp
                            : MOVEMENT_TYPE.adjustmentDown
                        );
                        setAmount(String(Math.abs(difference)));
                      }}
                    >
                      {t('inventory.useIt')}
                    </Button>
                    <p className="text-muted-foreground pb-2 text-xs">
                      {t('inventory.countedToHint')}
                    </p>
                  </div>
                </div>

                <Field
                  label={t('inventory.amount')}
                  htmlFor="amount"
                  hint={
                    isIncrease(type)
                      ? t('inventory.amountAddHint')
                      : t('inventory.amountRemoveHint')
                  }
                >
                  <Input
                    id="amount"
                    type="number"
                    min={0}
                    step="any"
                    value={amount}
                    disabled={busy}
                    onChange={(event) => setAmount(event.target.value)}
                  />
                </Field>

                <Field
                  label={t('inventory.reason')}
                  hint={t('inventory.reasonHint')}
                >
                  <Select
                    value={String(reason)}
                    disabled={busy}
                    onValueChange={(value) => setReason(Number(value))}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(MOVEMENT_REASON).map((value) => (
                        <SelectItem key={value} value={String(value)}>
                          {t(`inventory.reason${value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field
                  label={t('inventory.changedAt')}
                  htmlFor="changedAt"
                  hint={t('inventory.changedAtHint')}
                >
                  <Input
                    id="changedAt"
                    type="datetime-local"
                    value={changedAt}
                    disabled={busy}
                    onChange={(event) => setChangedAt(event.target.value)}
                  />
                </Field>

                <Field label={t('inventory.comments')} htmlFor="comments">
                  <Textarea
                    id="comments"
                    rows={2}
                    value={comments}
                    disabled={busy}
                    onChange={(event) => setComments(event.target.value)}
                  />
                </Field>

                {/* The arithmetic said out loud, because an adjustment down of
                    "5" on a count of 3 is a mistake worth seeing before it is
                    made rather than after. */}
                {canRegister && (
                  <p className="bg-muted text-muted-foreground rounded-xl p-3 text-sm">
                    {t('inventory.echo', {
                      from: formatNumber(data.quantityOnHand),
                      to: formatNumber(
                        isIncrease(type)
                          ? data.quantityOnHand + parsedAmount
                          : data.quantityOnHand - parsedAmount
                      ),
                    })}
                  </p>
                )}

                <Button
                  className="w-full"
                  disabled={!canRegister || busy}
                  onClick={submitMovement}
                >
                  {register.isPending && <Loader2 className="animate-spin" />}
                  {t('inventory.record')}
                </Button>
              </TabsContent>

              {/* --------------------------------------------- settings */}
              <TabsContent value="settings" className="space-y-4 pt-4">
                <Toggle
                  label={t('inventory.affectsInventory')}
                  hint={t('inventory.affectsInventoryHint')}
                  checked={data.affectsInventory}
                  disabled={busy}
                  onChange={(value) =>
                    flags.mutate(
                      { businessItemId: data.id, affectsInventory: value },
                      { onError: failed }
                    )
                  }
                />
                <Toggle
                  label={t('inventory.isVisible')}
                  hint={t('inventory.isVisibleHint')}
                  checked={data.isVisible}
                  disabled={busy}
                  onChange={(value) =>
                    flags.mutate(
                      { businessItemId: data.id, isVisible: value },
                      { onError: failed }
                    )
                  }
                />
                <Toggle
                  label={t('inventory.isAvailable')}
                  hint={t('inventory.isAvailableHint')}
                  checked={data.isAvailable}
                  disabled={busy}
                  onChange={(value) =>
                    flags.mutate(
                      { businessItemId: data.id, isAvailable: value },
                      { onError: failed }
                    )
                  }
                />

                <Section title={t('inventory.costs')}>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <Field label={t('inventory.cost')} htmlFor="cost">
                      <Input
                        id="cost"
                        type="number"
                        min={0}
                        step="0.01"
                        value={cost}
                        disabled={busy}
                        onChange={(event) => setCost(event.target.value)}
                      />
                    </Field>
                    <Field label={t('inventory.costOfBox')} htmlFor="costOfBox">
                      <Input
                        id="costOfBox"
                        type="number"
                        min={0}
                        step="0.01"
                        value={costOfBox}
                        disabled={busy}
                        onChange={(event) => setCostOfBox(event.target.value)}
                      />
                    </Field>
                    <Field
                      label={t('inventory.pack')}
                      htmlFor="pack"
                      hint={t('inventory.packHint')}
                    >
                      <Input
                        id="pack"
                        type="number"
                        min={0}
                        step="any"
                        value={pack}
                        disabled={busy}
                        onChange={(event) => setPack(event.target.value)}
                      />
                    </Field>
                  </div>
                  <Button
                    variant="outline"
                    disabled={busy}
                    onClick={() =>
                      flags.mutate(
                        {
                          businessItemId: data.id,
                          cost: Number(cost),
                          costOfBox: Number(costOfBox),
                          pack: Number(pack),
                        },
                        {
                          onSuccess: () =>
                            toast.success(t('inventory.costsSaved')),
                          onError: failed,
                        }
                      )
                    }
                  >
                    {flags.isPending && <Loader2 className="animate-spin" />}
                    {t('inventory.saveCosts')}
                  </Button>
                </Section>
              </TabsContent>

              {/* ---------------------------------------------- history */}
              <TabsContent value="history" className="space-y-3 pt-4">
                {history.isPending && (
                  <Skeleton className="h-40 w-full rounded-xl" />
                )}

                {history.isSuccess && rows.length === 0 && (
                  <p className="text-muted-foreground rounded-xl border border-dashed py-10 text-center text-sm">
                    {t('inventory.noHistory')}
                  </p>
                )}

                {rows.length > 0 && (
                  <>
                    <div className="relative rounded-xl">
                      <BusyOverlay show={history.isFetching} />
                      <div
                        className={cn(
                          'divide-y rounded-xl border',
                          history.isFetching && 'opacity-60 transition-opacity'
                        )}
                      >
                        {rows.map((movement) => {
                          const up = isIncrease(movement.type);
                          return (
                            <div
                              key={movement.id}
                              className="flex items-start gap-3 p-3 text-sm"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="font-medium">
                                  {t(`inventory.type${movement.type}`)}
                                  {movement.reason !== MOVEMENT_REASON.none && (
                                    <Badge variant="secondary" className="ml-2">
                                      {t(`inventory.reason${movement.reason}`)}
                                    </Badge>
                                  )}
                                </p>
                                <p className="text-muted-foreground text-xs">
                                  {formatDateTime(movement.createdAt)}
                                </p>
                                {movement.comments && (
                                  <p className="text-muted-foreground text-xs italic">
                                    {movement.comments}
                                  </p>
                                )}
                                {movement.registeredBy && (
                                  <div className="mt-1 flex items-center gap-1.5">
                                    <Avatar className="size-5">
                                      {movement.registeredBy.avatar && (
                                        <AvatarImage
                                          src={movement.registeredBy.avatar}
                                          alt=""
                                        />
                                      )}
                                      <AvatarFallback className="text-[0.5rem]">
                                        {initials(
                                          movement.registeredBy.firstName,
                                          movement.registeredBy.lastName
                                        )}
                                      </AvatarFallback>
                                    </Avatar>
                                    <span className="text-muted-foreground text-xs">
                                      {movement.registeredBy.completeName ??
                                        [
                                          movement.registeredBy.firstName,
                                          movement.registeredBy.lastName,
                                        ]
                                          .filter(Boolean)
                                          .join(' ')}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <p
                                className={
                                  up
                                    ? 'text-success shrink-0 tabular-nums'
                                    : 'shrink-0 tabular-nums'
                                }
                              >
                                {up ? '+' : '−'}
                                {formatNumber(Math.abs(movement.amount))}
                              </p>
                              {/* Sales came from the till; undoing one here would
                                  silently contradict the order behind it. */}
                              {movement.type !== MOVEMENT_TYPE.sale && (
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  aria-label={t('inventory.revert')}
                                  disabled={busy}
                                  onClick={() => setReverting(movement)}
                                >
                                  <Undo2 className="size-4" />
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <Pagination
                      page={historyPage}
                      size={PAGE_SIZE}
                      total={history.data?.total ?? 0}
                      onPageChange={setHistoryPage}
                    />
                  </>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}

        <BreakdownDialog
          item={data}
          open={breakingDown}
          onOpenChange={setBreakingDown}
        />

        <AlertDialog
          open={reverting !== null}
          onOpenChange={(next) => {
            if (!next) setReverting(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('inventory.revert')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('inventory.revertConfirm', {
                  amount: formatNumber(Math.abs(reverting?.amount ?? 0)),
                })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() =>
                  revert.mutate(reverting!.id, {
                    onSuccess: () => {
                      toast.success(t('inventory.reverted'));
                      setReverting(null);
                    },
                    onError: failed,
                  })
                }
              >
                {t('inventory.revert')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
}
